import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiFetch } from "@/lib/api";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailsDrawer } from "@/components/details-drawer";
import { EntityForm } from "@/components/entity-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface BranchDto {
  id: number; branchName: string; branchTypeId: number | null; managerEmployeeId: number | null;
  branchPrefix: string | null; email: string | null; phone: string | null; mobile: string | null;
  departmentCode: string | null; deleted: boolean;
}
interface Paged<T> { items: T[]; page: number; pageSize: number; totalCount: number; }

const schema = z.object({
  branchName: z.string().min(1).max(100),
  branchTypeId: z.number().int().positive().nullable().optional(),
  managerEmployeeId: z.number().int().positive().nullable().optional(),
  branchPrefix: z.string().max(4).nullable().optional(),
  email: z.string().email().or(z.literal("")).nullable().optional(),
  phone: z.string().max(20).nullable().optional(),
  mobile: z.string().max(20).nullable().optional(),
  departmentCode: z.string().max(20).nullable().optional(),
});
type FormValues = z.infer<typeof schema>;

export function BranchesPage() {
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState<BranchDto | null>(null);
  const [creating, setCreating] = useState(false);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["branches", page],
    queryFn: () => apiFetch<Paged<BranchDto>>(`/api/branches?page=${page}&pageSize=50`),
  });

  const upsert = useMutation({
    mutationFn: async (values: FormValues) => {
      const body = JSON.stringify(values);
      return editing
        ? apiFetch<BranchDto>(`/api/branches/${editing.id}`, { method: "PATCH", body })
        : apiFetch<BranchDto>("/api/branches", { method: "POST", body });
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["branches"] }); setEditing(null); setCreating(false); },
  });

  const cols: DataGridColumn<BranchDto>[] = [
    { id: "name",  header: "Name",   accessor: (r) => <button className="underline" onClick={() => setEditing(r)}>{r.branchName}</button> },
    { id: "code",  header: "Prefix", accessor: (r) => r.branchPrefix ?? "—" },
    { id: "email", header: "Email",  accessor: (r) => r.email ?? "—" },
    { id: "phone", header: "Phone",  accessor: (r) => r.phone ?? "—" },
  ];

  return (
    <ListPage title="Branches" primaryAction={<IntentButton onClick={() => setCreating(true)}>New branch</IntentButton>}>
      <DataGrid rows={data?.items ?? []} columns={cols} rowKey={(r) => r.id}
        totalRows={data?.totalCount ?? 0} pageIndex={page} pageSize={50}
        loading={isLoading} onPageChange={setPage} />
      <DetailsDrawer
        open={creating || editing !== null}
        onOpenChange={(o) => { if (!o) { setEditing(null); setCreating(false); } }}
        title={editing ? "Edit branch" : "New branch"}>
        <EntityForm
          schema={schema}
          defaultValues={editing ?? { branchName: "", branchTypeId: null, managerEmployeeId: null, branchPrefix: "", email: "", phone: "", mobile: "", departmentCode: "" }}
          onSubmit={(v) => upsert.mutate(v)}>
          {(f) => (
            <div className="space-y-3">
              <div><Label htmlFor="bn">Name</Label><Input id="bn" {...f.register("branchName")} /></div>
              <div><Label htmlFor="bp">Prefix (max 4)</Label><Input id="bp" {...f.register("branchPrefix")} /></div>
              <div><Label htmlFor="em">Email</Label><Input id="em" type="email" {...f.register("email")} /></div>
              <div><Label htmlFor="ph">Phone</Label><Input id="ph" {...f.register("phone")} /></div>
              <div><Label htmlFor="mo">Mobile</Label><Input id="mo" {...f.register("mobile")} /></div>
              <div><Label htmlFor="dc">Department code</Label><Input id="dc" {...f.register("departmentCode")} /></div>
              <div><Label htmlFor="bt">Branch type ID</Label><Input id="bt" type="number" {...f.register("branchTypeId")} /></div>
              <div><Label htmlFor="me">Manager employee ID</Label><Input id="me" type="number" {...f.register("managerEmployeeId")} /></div>
              <Button type="submit" disabled={upsert.isPending}>{editing ? "Save" : "Create"}</Button>
            </div>
          )}
        </EntityForm>
      </DetailsDrawer>
    </ListPage>
  );
}
